import React from 'react'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Overview = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Events are gatherings hosted by{' '}
            <Link href='/docs/guilds/overview'>Guilds</Link> on Guild. Whether
            you are organizing a local meetup, an online workshop, or a hybrid
            conference, Events give you the tools to bring people together and
            manage the full attendee experience.
          </Trans>
        </Paragraph>
        <Paragraph size='$5'>
          <Trans>Guild supports three types of Events:</Trans>
        </Paragraph>
        <YStack gap='$3'>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon
              name='location'
              size='$5'
              color='$color'
              flexShrink={0}
              marginTop={2}
            />
            <Paragraph size='$5'>
              <Trans>
                <Paragraph size='$5' fontWeight='$7'>
                  In-Person
                </Paragraph>{' '}
                — Events held at a physical venue. You can set a venue address
                that attendees see as a clickable map link, configure capacity
                limits, and enable a waitlist when the venue is full.
              </Trans>
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon
              name='globe'
              size='$5'
              color='$color'
              flexShrink={0}
              marginTop={2}
            />
            <Paragraph size='$5'>
              <Trans>
                <Paragraph size='$5' fontWeight='$7'>
                  Online
                </Paragraph>{' '}
                — Virtual Events with a meeting link (Zoom, Google Meet, or any
                URL). The link is shared with attendees after they RSVP or
                purchase a ticket, keeping it private from the public Event
                page.
              </Trans>
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon
              name='people'
              size='$5'
              color='$color'
              flexShrink={0}
              marginTop={2}
            />
            <Paragraph size='$5'>
              <Trans>
                <Paragraph size='$5' fontWeight='$7'>
                  Hybrid
                </Paragraph>{' '}
                — Events that combine both in-person and online attendance.
                Attendees choose how they want to participate when they RSVP,
                and organizers can track capacity for each format separately.
              </Trans>
            </Paragraph>
          </XStack>
        </YStack>
      </Card>

      <Card gap='$4'>
        <DocSection id='event-page-features' title={t`Event Page Features`}>
          <Paragraph size='$5'>
            <Trans>
              Every Event has a dedicated{' '}
              <Link href='/docs/events/page'>Event page</Link> with a cover
              photo, title, date and time, venue or online details, a
              description, and a list of attendees. Organizers can add{' '}
              <Link href='/docs/presentations/overview'>Presentations</Link> to
              build a schedule, and{' '}
              <Link href='/docs/events/messages'>send messages</Link> to keep
              attendees informed.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              Events can be free or paid. For paid Events, Guild integrates with
              Stripe to handle{' '}
              <Link href='/docs/events/ticketing-overview'>
                Ticketing & Payments
              </Link>
              , including multiple ticket tiers, discount codes, and order
              management.
            </Trans>
          </Paragraph>
          <DocScreenshot
            src='docs/events/event-page.png'
            alt={t`Event page for Code Buddy Club showing cover photo, event title, host Guild, date and time, attendee avatars, and Attend button`}
            caption={t`An Event page with all the details attendees need to RSVP`}
          />
        </DocSection>
      </Card>
    </>
  )
}

export default Overview
